import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../../core/services/api/api";
import IconButton from "../../../../../../../base/button/icon_button";
import {Add, ArrowBack, Remove} from "@mui/icons-material";
import Button from "../../../../../../../base/button/button";
import {Col} from "reactstrap";
import {createUUId} from "../../../../../../../../../core/services/utils/utils";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnHighlightingMechanisms, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../../containers/data-grid";
import {EditPartSupportRequestWarehousesDataGridColumnNames} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../../../../../../app-specific/draggable-popover/product-information-popover";
import {EditPartSupportRequestProcessSteps} from "../../../../index";

const EditPartSupportRequestProductInfoSectionWarehousesSection = ({
                                                                       productInfo,
                                                                       setProductInfo,
                                                                       warehouse,
                                                                       setProcess
                                                                   }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const isSerialized = useMemo(() => !!productInfo?.isSerialized ?? true, [productInfo?.isSerialized]);

    const dataGridSaveConfig = useMemo(() =>
            isSerialized
                ? ({
                    name: 'EditPartSupportRequestProductInfoSectionWarehousesSectionSerializedDataGrid',
                    version: 1,
                })
                : ({
                    name: 'EditPartSupportRequestProductInfoSectionWarehousesSectionUnSerializedDataGrid',
                    version: 1,
                })
        , [isSerialized])

    /**
     * Determines the table columns to be shown in the table.
     *
     * * if not serializable, then returns the columns for un-serialized products
     * * otherwise returns the columns for serialized products
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() => {
        if (!isSerialized) {
            return [
                {
                    title: 'Warehouse',
                    name: EditPartSupportRequestWarehousesDataGridColumnNames.warehouse,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: 'Part No',
                    name: EditPartSupportRequestWarehousesDataGridColumnNames.partNo,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: 'Available Qty',
                    name: EditPartSupportRequestWarehousesDataGridColumnNames.availableQty,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: 'Selected Qty',
                    name: EditPartSupportRequestWarehousesDataGridColumnNames.selectedQty,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 150,
                },
                {
                    name: EditPartSupportRequestWarehousesDataGridColumnNames.select,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                },
            ]
        }
        return [
            {
                title: 'Warehouse',
                name: EditPartSupportRequestWarehousesDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Part No',
                name: EditPartSupportRequestWarehousesDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: 'Serial No',
                name: EditPartSupportRequestWarehousesDataGridColumnNames.serialNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                highlightingMechanism: DataGridColumnHighlightingMechanisms.diff,
                sortable: false,
                width: 150,
            },
            {
                name: EditPartSupportRequestWarehousesDataGridColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
        ]
    }, [isSerialized])

    const [dataGridColumns, _, __, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * With each change in the part of the lookup values:
     * - searches for the warehouse entries of the given part.
     */
    useEffect(() => {
        if (!productInfo?.part) return;
        getWarehouseParts().then();
    }, [productInfo?.part])

    /**
     * Fetches a list of parts that exists in the provided warehouse form the server.
     * * if the response of the api is successful, sets the state list to populate the tables.
     */
    const getWarehouseParts = async () => {
        setLoading(true);
        const forApi = {
            partNo: productInfo.part.partNumber,
            warehouseCode: warehouse.code,
        }
        const response = await Api.getPartSerialNosAndQuantityByWarehouse(forApi)
        if (!isMounted())
            return;
        const res = [];
        if (response?.resultFlag) {
            // if serialized
            if (!!response?.data?.serials) {
                response?.data?.serials?.forEach(serialNo => {
                    res.push({
                        id: createUUId(),
                        partNo: productInfo.part.partNumber,
                        warehouse: warehouse.name,
                        serialNo: serialNo,
                    })
                })
            } else {
                res.push({
                    id: createUUId(),
                    partNo: productInfo.part.partNumber,
                    warehouse: warehouse.name,
                    availableQty: response?.data?.availableQty ?? 0,
                    selectedQuantity: 0,
                })
            }
        }
        setProductInfo(prevState => ({
            ...prevState,
            isSerialized: !!response?.data?.serials
        }));
        setData(res);
        setLoading(false);
    }

    /**
     * Based on the serialize-ability of the selected part in the previous step:
     *
     * - either saves the warehouse information and navigates to the next step or
     * - shows the quantity table for the selected invoice.
     * @param entry
     */
    const onTableEntrySelected = (entry) => {
        if (isSerialized) {
            setProductInfo(prevState => ({
                ...prevState,
                selectedQuantity: null,
                serialNumber: entry.serialNo,
                invoiceNo: null,
                warehouseCode: entry?.warehouseCode,
            }))
        } else {
            setProductInfo(prevState => ({
                ...prevState,
                selectedQuantity: entry.selectedQuantity,
                serialNumber: null,
                invoiceNo: null,
                warehouseCode: entry.warehouseCode,
            }))
        }
        return setProcess(EditPartSupportRequestProcessSteps.review);
    }

    /**
     * Changes the selected quantity of the given entry with the newValue.
     * @param newValue {number}
     * @param entry {any}
     */
    const changeSelectedQuantity = (newValue, entry) => {
        const selectedQuantity = !newValue
            ? null
            : Math.max(
                0,
                Math.min(
                    parseFloat(entry.availableQty ?? '0'),
                    newValue
                )
            );
        setData(prevState => prevState?.map(e => e.id === entry.id
            ? {...e, selectedQuantity: selectedQuantity}
            : e
        ));
    }

    /**
     * Navigates the user back to the part selection.
     */
    const goBack = () => {
        setProductInfo(prevState => ({
            ...prevState,
            showProductOrSerialNoSelection: false,
            selectedQuantity: null,
            serialNo: null,
            invoiceNo: null,
            warehouseCode: null,
        }))
    }

    /**
     * Creates the data entries for the table
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return data?.map((entry) => {
            return ({
                key: entry?.id,
                data: entry,
                cells: {
                    [EditPartSupportRequestWarehousesDataGridColumnNames.warehouse]: entry.warehouse,
                    [EditPartSupportRequestWarehousesDataGridColumnNames.partNo]:
                        <ProductInformationPopover partNo={entry.partNo}>
                            {entry.partNo ?? ""}
                        </ProductInformationPopover>,
                    [EditPartSupportRequestWarehousesDataGridColumnNames.serialNo]: entry.serialNo,
                    [EditPartSupportRequestWarehousesDataGridColumnNames.availableQty]: entry.availableQty,
                    [EditPartSupportRequestWarehousesDataGridColumnNames.selectedQty]:
                        <div className='d-flex w-100 align-items-center justify-content-center'>
                            <IconButton
                                onPressed={() => changeSelectedQuantity((entry.selectedQuantity ?? 0) - 1, entry)}
                                color={'secondary'}
                            >
                                <Remove className={'icon small'}/>
                            </IconButton>
                            <p {...dataGridCellTextProps} className={'text-md mx-4 secondary-color-1 font-weight-semi-bold'}>
                                {entry.selectedQuantity ?? 0}
                            </p>
                            <IconButton
                                onPressed={() => changeSelectedQuantity((entry.selectedQuantity ?? 0) + 1, entry)}
                                color={'secondary'}
                            >
                                <Add className={'icon small'}/>
                            </IconButton>
                        </div>,
                    [EditPartSupportRequestWarehousesDataGridColumnNames.select]:
                        <Button
                            className={'px-4 py-1'}
                            variant={'outlined'}
                            color={'secondary'}
                            onClick={() => onTableEntrySelected(entry)}
                        >
                            Select
                        </Button>,
                },
            })
        })
    }, [data])

    return (
        <>
            <Col xs={12} className={'row no-gutters align-items-start justify-content-start mb-4'}>
                <IconButton
                    color={'secondary'}
                    onPressed={goBack}
                    style={{marginTop: 2}}
                >
                    <ArrowBack className={'icon normal'}/>
                </IconButton>
                <div className={'d-flex flex-column'}>
                    {
                        !(!isSerialized && !loading) &&
                        <ProductInformationPopover
                            partNo={productInfo?.part?.partNumber}
                            wrapperProps={{className: 'ml-3'}}
                            buttonProps={{className: ' pl-0'}}
                        >
                            <span className={'text-md'}>
                            {productInfo?.part?.partNumber ?? '--'}
                            </span>
                        </ProductInformationPopover>
                    }
                    {
                        !isSerialized && !loading &&
                        <p className={'text-md font-weight-semi-bold text-color-1 ml-3 part-number-fluid'}>
                            how many <ProductInformationPopover partNo={productInfo?.part?.partNumber}
                                                                wrapperProps={{className: 'd-inline-block'}}>
                            {productInfo?.part?.partNumber ?? '--'}
                        </ProductInformationPopover> would you like to apply for?
                        </p>
                    }
                    <p className={'text-sm grey-3 ml-3'}>
                        Back to lookup
                    </p>
                </div>
            </Col>
            <Col xs={12} className={'mb-4'}>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    rows={dataGridRows}
                    columns={dataGridColumns}
                    hideExporting
                    hideFooterActions
                    hideFooter
                    visibleRows={5}
                    state={{
                        loading: {state: loading},
                        density: dataGridDensity,
                    }}
                />
            </Col>
        </>
    );
}


export default EditPartSupportRequestProductInfoSectionWarehousesSection;
